import { isPlainObject, snakeCase, transform } from 'lodash';
import { stringify } from 'qs';

import type { AnyObj } from '../../../../typescript';
import { stripEmpty } from '../../strip-empty';

const toSnakeCaseDeep = <T extends AnyObj>(obj: T): AnyObj =>
    transform(obj, (result, value, key) => {
        const snakeKey = snakeCase(key);

        if (isPlainObject(value)) {
            result[snakeKey] = toSnakeCaseDeep(value);
        } else if (Array.isArray(value)) {
            result[snakeKey] = value.map(item => (isPlainObject(item) ? toSnakeCaseDeep(item) : item));
        } else {
            result[snakeKey] = value;
        }
    });

/**
 * Prepares request body/query params for v2 research API.
 */
const formatV2RequestParams = <T extends AnyObj | FormData>(params: T): T extends FormData ? FormData : AnyObj => {
    if (params instanceof FormData) {
        const modifiedFormData = new FormData();
        for (const [key, value] of params.entries()) {
            const snakeKey = snakeCase(key);
            modifiedFormData.append(snakeKey, value);
        }
        return modifiedFormData;
    }
    return toSnakeCaseDeep(params) as T extends FormData ? FormData : AnyObj;
};

const formatV2SearchParams = (params: AnyObj) => {
    return stringify(stripEmpty(params), {
        skipNulls: true,
        addQueryPrefix: true,
        arrayFormat: 'comma',
    });
};

export { formatV2RequestParams, formatV2SearchParams };
